import React, { useRef, useEffect, useMemo, useState, useCallback } from 'react'
import { GoalDropdown } from './GoalDropdown.jsx'

const STATUS_CYCLE = [null, 'not-started', 'in-progress', 'blocked', 'wrapping-up']

const STATUS_CONFIG = {
  'not-started': { label: 'not started', bg: '#f1efe8', color: '#444441' },
  'in-progress': { label: 'in progress', bg: '#FAEEDA', color: '#633806' },
  blocked: { label: 'blocked', bg: '#FCEBEB', color: '#A32D2D' },
  'wrapping-up': { label: 'wrapping up', bg: '#E1F5EE', color: '#0F6E56' },
}

function nextStatus(current) {
  const idx = STATUS_CYCLE.indexOf(current)
  return STATUS_CYCLE[(idx + 1) % STATUS_CYCLE.length]
}

function generateId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return `p_${crypto.randomUUID().slice(0, 8)}`
  }
  return `p_${Math.random().toString(36).slice(2, 10)}`
}

function RankBadge({ rank, emphasized }) {
  const baseStyle = {
    width: 22,
    height: 22,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 11,
    fontWeight: 600,
    flexShrink: 0,
    marginTop: 1,
  }

  const style = emphasized
    ? { ...baseStyle, background: '#185FA5', color: 'white' }
    : { ...baseStyle, background: 'white', border: '1.5px solid #185FA5', color: '#185FA5' }

  return (
    <div style={style} aria-hidden="true">
      {rank}
    </div>
  )
}

function autoResize(el) {
  if (!el) return
  el.style.height = 'auto'
  el.style.height = el.scrollHeight + 'px'
}

function TextInput({ value, placeholder, onChange, disabled, inputRef }) {
  const ref = useRef(null)
  const setRef = useCallback((el) => {
    ref.current = el
    if (typeof inputRef === 'function') inputRef(el)
    else if (inputRef) inputRef.current = el
  }, [inputRef])

  useEffect(() => { autoResize(ref.current) }, [value])

  return (
    <textarea
      ref={setRef}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      onInput={(e) => autoResize(e.target)}
      disabled={disabled}
      rows={1}
      style={{
        flex: 1,
        minWidth: 0,
        fontSize: 13,
        lineHeight: 1.4,
        color: 'var(--text-primary)',
        background: 'transparent',
        border: 'none',
        outline: 'none',
        padding: 0,
        fontFamily: 'inherit',
        resize: 'none',
        overflowY: 'hidden',
        minHeight: '18px',
      }}
      aria-label="Priority text"
    />
  )
}

function StatusChip({ value, onCycle, disabled }) {
  if (value === null) {
    return (
      <button
        type="button"
        onClick={onCycle}
        disabled={disabled}
        style={{
          fontSize: 8,
          fontWeight: 500,
          padding: '1px 5px',
          background: 'transparent',
          color: '#8A8780',
          border: '0.5px dashed #c8ccd4',
          borderRadius: 3,
          textTransform: 'uppercase',
          letterSpacing: '0.03em',
          cursor: disabled ? 'default' : 'pointer',
          flexShrink: 0,
          fontFamily: 'inherit',
          lineHeight: 1.4,
        }}
        aria-label="Add status"
      >
        + status
      </button>
    )
  }

  const config = STATUS_CONFIG[value]
  return (
    <button
      type="button"
      onClick={onCycle}
      disabled={disabled}
      style={{
        fontSize: 8,
        fontWeight: 500,
        padding: '1px 5px',
        background: config.bg,
        color: config.color,
        border: 'none',
        borderRadius: 3,
        textTransform: 'uppercase',
        letterSpacing: '0.03em',
        cursor: disabled ? 'default' : 'pointer',
        flexShrink: 0,
        fontFamily: 'inherit',
        lineHeight: 1.4,
      }}
      aria-label={`Status: ${config.label}. Click to change.`}
    >
      {config.label}
    </button>
  )
}

function GoalLinkDisplay({ goal, onChange, activeGoals, disabled }) {
  return (
    <div
      style={{
        fontSize: 10,
        color: '#185FA5',
        display: 'flex',
        alignItems: 'center',
        gap: 4,
      }}
    >
      <svg width="10" height="10" viewBox="0 0 12 12" aria-hidden="true">
        <path
          d="M6 1 L7.5 4.5 L11 5 L8.5 7.5 L9 11 L6 9.5 L3 11 L3.5 7.5 L1 5 L4.5 4.5 Z"
          fill="#185FA5"
        />
      </svg>
      <GoalDropdown
        selectedGoalId={goal.goalId}
        activeGoals={activeGoals}
        allowClear={true}
        onSelect={(goalId) => onChange(goalId)}
        disabled={disabled}
        triggerStyle={{
          background: 'none',
          border: 'none',
          color: '#185FA5',
          fontSize: 10,
          padding: 0,
          cursor: disabled ? 'default' : 'pointer',
          fontFamily: 'inherit',
        }}
        triggerLabel={goal.title}
      />
    </div>
  )
}

function GoalLinkButton({ onSelect, activeGoals }) {
  return (
    <div style={{ fontSize: 10, color: 'var(--text-secondary)' }}>
      <GoalDropdown
        selectedGoalId={null}
        activeGoals={activeGoals}
        allowClear={false}
        onSelect={onSelect}
        triggerStyle={{
          background: 'none',
          border: 'none',
          color: 'var(--text-secondary)',
          fontSize: 10,
          padding: 0,
          cursor: 'pointer',
          fontFamily: 'inherit',
          opacity: 0.6,
        }}
        triggerLabel="+ link to goal"
      />
    </div>
  )
}

function ReorderChevrons({ canMoveUp, canMoveDown, onMoveUp, onMoveDown, disabled }) {
  return (
    <div
      className="reorder-chevrons"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        flexShrink: 0,
        opacity: 0,
        transition: 'opacity var(--duration-fast) ease',
      }}
    >
      <button
        type="button"
        onClick={onMoveUp}
        disabled={!canMoveUp || disabled}
        style={{
          width: 16,
          height: 12,
          padding: 0,
          background: 'none',
          border: 'none',
          cursor: (!canMoveUp || disabled) ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: (!canMoveUp || disabled) ? '#c8ccd4' : '#5F5E5A',
        }}
        aria-label="Move up"
      >
        <svg width="10" height="6" viewBox="0 0 10 6" aria-hidden="true">
          <path d="M1 5 L5 1 L9 5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      <button
        type="button"
        onClick={onMoveDown}
        disabled={!canMoveDown || disabled}
        style={{
          width: 16,
          height: 12,
          padding: 0,
          background: 'none',
          border: 'none',
          cursor: (!canMoveDown || disabled) ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: (!canMoveDown || disabled) ? '#c8ccd4' : '#5F5E5A',
        }}
        aria-label="Move down"
      >
        <svg width="10" height="6" viewBox="0 0 10 6" aria-hidden="true">
          <path d="M1 1 L5 5 L9 1" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    </div>
  )
}

function DeleteButton({ onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="priority-delete-btn"
      style={{
        marginLeft: 4,
        padding: '2px 4px',
        background: 'none',
        border: 'none',
        cursor: disabled ? 'default' : 'pointer',
        color: 'var(--text-secondary)',
        fontSize: 14,
        lineHeight: 1,
        flexShrink: 0,
        opacity: 0,
        transition: 'opacity var(--duration-fast) ease',
      }}
      aria-label="Delete priority"
    >
      ×
    </button>
  )
}

function PriorityRow({
  item,
  rank,
  canMoveUp,
  canMoveDown,
  activeGoals,
  onChange,
  onDelete,
  onMoveUp,
  onMoveDown,
  disabled,
  inputRef,
}) {
  const isRankOne = rank === 1
  const linkedGoal = activeGoals?.find((g) => g.goalId === item.goalId) ?? null
  const hasGoalSection = linkedGoal !== null

  return (
    <div
      className="priority-row"
      style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}
      role="group"
      aria-label={`Priority ${rank}: ${item.text || 'unnamed'}`}
    >
      <RankBadge rank={rank} emphasized={isRankOne} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 6,
            marginBottom: hasGoalSection || (!hasGoalSection && activeGoals?.length > 0 && !disabled) ? 2 : 0,
            minWidth: 0,
          }}
        >
          <TextInput
            inputRef={inputRef}
            value={item.text}
            placeholder={rank === 1 ? 'Your most important thing\u2026' : `Priority ${rank}\u2026`}
            onChange={(text) => onChange({ ...item, text })}
            disabled={disabled}
          />
        </div>

        {hasGoalSection && (
          <GoalLinkDisplay
            goal={linkedGoal}
            onChange={(goalId) => onChange({ ...item, goalId })}
            activeGoals={activeGoals}
            disabled={disabled}
          />
        )}

        {!hasGoalSection && activeGoals && activeGoals.length > 0 && !disabled && (
          <GoalLinkButton
            onSelect={(goalId) => onChange({ ...item, goalId })}
            activeGoals={activeGoals}
          />
        )}
      </div>

      <ReorderChevrons
        canMoveUp={canMoveUp}
        canMoveDown={canMoveDown}
        onMoveUp={onMoveUp}
        onMoveDown={onMoveDown}
        disabled={disabled}
      />

      <DeleteButton onClick={onDelete} disabled={disabled} />
    </div>
  )
}

function AddButton({ onClick, label, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        marginLeft: 32,
        width: 'calc(100% - 32px)',
        fontSize: 12,
        fontWeight: 500,
        color: '#185FA5',
        background: 'none',
        border: '0.5px dashed #c8d6ea',
        borderRadius: 6,
        padding: '6px 0',
        cursor: disabled ? 'default' : 'pointer',
        fontFamily: 'inherit',
        opacity: disabled ? 0.4 : 1,
      }}
      aria-label={label}
    >
      {label}
    </button>
  )
}

export function PrioritiesList({ value, onChange, maxItems = 3, activeGoals = [], disabled = false }) {
  const items = useMemo(() => (value?.items ?? []).slice(0, maxItems), [value, maxItems])
  const [focusIdx, setFocusIdx] = useState(null)
  const inputRefs = useRef({})

  useEffect(() => {
    if (focusIdx != null && inputRefs.current[focusIdx]) {
      inputRefs.current[focusIdx].focus()
      setFocusIdx(null)
    }
  }, [focusIdx, items.length])

  const hasPlaceholder = items.length === 0
  const renderItems = useMemo(() => {
    if (items.length === 0) {
      return [{ id: 'placeholder-1', text: '', status: null, goalId: null, isPlaceholder: true }]
    }
    return items
  }, [items])

  const handleRowChange = useCallback((idx, updated) => {
    if (hasPlaceholder && idx === 0) {
      // Materialize placeholder into a real item
      onChange({ items: [{ id: generateId(), text: updated.text, status: updated.status, goalId: updated.goalId }] })
      return
    }
    const next = items.map((item, i) => i === idx ? updated : item)
    onChange({ items: next })
  }, [items, hasPlaceholder, onChange])

  const handleDelete = useCallback((idx) => {
    if (hasPlaceholder) return
    onChange({ items: items.filter((_, i) => i !== idx) })
  }, [items, hasPlaceholder, onChange])

  const handleAdd = useCallback(() => {
    if (hasPlaceholder) {
      // Materialize placeholder as empty slot 1 + add slot 2
      const next = [
        { id: generateId(), text: '', status: null, goalId: null },
        { id: generateId(), text: '', status: null, goalId: null },
      ]
      onChange({ items: next })
      setFocusIdx(1)
      return
    }
    if (items.length >= maxItems) return
    const next = [...items, { id: generateId(), text: '', status: null, goalId: null }]
    onChange({ items: next })
    setFocusIdx(next.length - 1)
  }, [items, maxItems, hasPlaceholder, onChange])

  const handleMove = useCallback((from, to) => {
    if (to < 0 || to >= items.length) return
    const next = [...items]
    const temp = next[from]
    next[from] = next[to]
    next[to] = temp
    onChange({ items: next })
  }, [items, onChange])

  const addLabel = `+ add priority ${renderItems.length + (hasPlaceholder ? 1 : 1)}`

  return (
    <div style={{ padding: '8px 14px 12px', display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {renderItems.map((item, idx) => (
          <PriorityRow
            key={item.id}
            item={item}
            rank={idx + 1}
            canMoveUp={idx > 0 && !hasPlaceholder}
            canMoveDown={idx < renderItems.length - 1 && !hasPlaceholder}
            activeGoals={activeGoals}
            onChange={(next) => handleRowChange(idx, next)}
            onDelete={() => handleDelete(idx)}
            onMoveUp={() => handleMove(idx, idx - 1)}
            onMoveDown={() => handleMove(idx, idx + 1)}
            disabled={disabled}
            inputRef={(el) => { inputRefs.current[idx] = el }}
          />
        ))}

        {renderItems.length < maxItems && (
          <AddButton
            onClick={handleAdd}
            label={`+ add priority ${renderItems.length + 1}`}
            disabled={disabled}
          />
        )}
      </div>

      {items.length === 0 && (
        <div style={{ fontSize: 10, color: 'var(--text-secondary)', textAlign: 'center', letterSpacing: '0.02em' }}>
          Up to 3 priorities
        </div>
      )}
    </div>
  )
}
